/*
  "What's new" (workflow 22, architecture 13.4). The server works out the net change for the reader. What the
  screen adds: the order (returned work first), the short list of about eight with the rest on request, and
  the words.
*/

import type { WhatsNew, WhatsNewEntry, WhatsNewGroup } from "../../../api/types";

export type Change = WhatsNewEntry["change"];

export const CHANGE_WORDS: Record<Change, string> = {
  added: "Added",
  changed: "Changed",
  removed: "Removed",
  validated: "Validated",
  sent_back: "Sent back",
  rejected: "Rejected",
  handed_over: "Handed over to the SME",
};

/** Work that came back to the engineer shows before everything else. */
export function isReturned(change: Change): boolean {
  return change === "sent_back" || change === "rejected";
}

/** How many entries are shown before "show the rest". */
export const SHORT_LIST = 8;

/** Inside a group the entries run by state: returned work first, then what waits for the SME, then the rest. */
const ORDER: Record<Change, number> = { sent_back: 0, rejected: 1, handed_over: 2, added: 3, changed: 4, validated: 5, removed: 6 };

/**
 * Returned work first, in the groups and inside each group (architecture 13.4: "grouped by item ... then by state,
 * with returned elements first"). Inside a group the entries run by state, and the order of the server holds
 * between entries of one state.
 */
export function orderWhatsNew(news: WhatsNew): WhatsNew {
  const groups = news.groups.map((group) => ({
    ...group,
    entries: group.entries
      .map((entry, index) => ({ entry, index }))
      .sort((a, b) => ORDER[a.entry.change] - ORDER[b.entry.change] || a.index - b.index)
      .map(({ entry }) => entry),
  }));
  const returned = groups.filter((g) => g.entries.some((e) => isReturned(e.change)));
  const others = groups.filter((g) => !g.entries.some((e) => isReturned(e.change)));
  return { ...news, groups: [...returned, ...others] };
}

export function countEntries(groups: readonly WhatsNewGroup[]): number {
  return groups.reduce((total, group) => total + group.entries.length, 0);
}

export interface ShortList {
  groups: WhatsNewGroup[];
  /** The entries left out: the ones past the limit, and those the server did not send. */
  hidden: number;
  /** Of `hidden`, the entries the browser has and can show at once. */
  held: number;
}

/** The first `limit` entries in the order shown, keeping the groups they sit in. */
export function shortList(news: WhatsNew, limit: number = SHORT_LIST): ShortList {
  const groups: WhatsNewGroup[] = [];
  let left = limit;
  let held = 0;
  for (const group of news.groups) {
    if (left <= 0) {
      held += group.entries.length;
      continue;
    }
    const entries = group.entries.slice(0, left);
    left -= entries.length;
    held += group.entries.length - entries.length;
    if (entries.length > 0) groups.push({ ...group, entries });
  }
  return { groups, held, hidden: held + Math.max(0, news.more) };
}

/** One sentence about the size of the news: "6 changes". */
export function sizeWords(news: WhatsNew): string {
  const total = countEntries(news.groups) + Math.max(0, news.more);
  if (total === 0) return "No changes";
  return total === 1 ? "1 change" : `${total} changes`;
}
